import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import {
  AverageLastScoreSpan,
  AveragePlayerScore,
  Position,
} from '@sorare/core/src/__generated__/globalTypes';
import { usePrevious } from '@sorare/core/src/hooks/usePrevious';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';

import type { Filters } from '#src/lib/performance.tsx';
import { ALL_VALUE, FilterKey } from '#src/lib/performance.tsx';

import type {
  usePerformanceFilters_anyCard,
  usePerformanceFilters_anyPlayer,
} from './__generated__/usePerformanceFilters.graphql.ts';

const replaceAllValue = (value: string) =>
  value === ALL_VALUE ? undefined : value;

const getAveragePlayerSpanFromScore = (
  score: TotalAveragePlayerScore
): AverageLastScoreSpan =>
  ({
    [AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE]:
      AverageLastScoreSpan.LAST_FIVE,
    [AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE]:
      AverageLastScoreSpan.LAST_TEN,
    [AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE]:
      AverageLastScoreSpan.LAST_FIFTEEN,
    [AveragePlayerScore.LAST_FORTY_SO5_AVERAGE_SCORE]:
      AverageLastScoreSpan.LAST_FORTY,
    [AveragePlayerScore.SEASON_AVERAGE_SCORE]: AverageLastScoreSpan.SEASON,
  })[score];

export const usePerformanceFilters = (
  player: Nullable<usePerformanceFilters_anyPlayer>,
  card?: Nullable<usePerformanceFilters_anyCard>,
  initialTeam?: string
) => {
  const initPosition =
    card?.anyPositions[0] || player?.cardPositions[0] || Position.Unknown;
  const previousPosition = usePrevious(initPosition);
  const initTeam = initialTeam ?? ALL_VALUE;
  const previousTeam = usePrevious(initTeam);

  const [filters, setFilters] = useState<Filters>({
    [FilterKey.COMPETITION]: ALL_VALUE,
    [FilterKey.TEAM]: initTeam,
    [FilterKey.HOME_AWAY]: ALL_VALUE,
    [FilterKey.MODE]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [FilterKey.POSITION]:
      card?.anyPositions[0] || player?.cardPositions[0] || Position.Unknown,
  });

  const handleFilterChange = (key: FilterKey, value: Filters[FilterKey]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (
    initPosition &&
    previousPosition &&
    initPosition !== previousPosition &&
    initPosition !== filters[FilterKey.POSITION]
  ) {
    setFilters(prev => ({ ...prev, [FilterKey.POSITION]: initPosition }));
  }

  if (
    previousTeam !== undefined &&
    initTeam !== previousTeam &&
    initTeam !== filters[FilterKey.TEAM]
  ) {
    setFilters(prev => ({ ...prev, [FilterKey.TEAM]: initTeam }));
  }

  const gqlFilters = {
    competitionSlug: replaceAllValue(filters[FilterKey.COMPETITION]),
    teamSlug: replaceAllValue(filters[FilterKey.TEAM]),
    location:
      filters[FilterKey.HOME_AWAY] === ALL_VALUE
        ? undefined
        : filters[FilterKey.HOME_AWAY],
    span: getAveragePlayerSpanFromScore(filters[FilterKey.MODE]),
  };

  return { filters, handleFilterChange, gqlFilters };
};

usePerformanceFilters.fragments = {
  anyPlayer: gql`
    fragment usePerformanceFilters_anyPlayer on AnyPlayerInterface {
      slug
      cardPositions
    }
  ` as TypedDocumentNode<usePerformanceFilters_anyPlayer>,
  anyCard: gql`
    fragment usePerformanceFilters_anyCard on AnyCardInterface {
      slug
      anyPositions
    }
  ` as TypedDocumentNode<usePerformanceFilters_anyCard>,
};
